import type { AuditLogEntry } from '@/userManagement/domain/entities/AuditLogEntry';
import type { AuditAction } from '@/userManagement/domain/types/AuditAction';
import type { ChangeCategory } from '@/userManagement/domain/types/ChangeCategory';

type AuditUserRef = {
  id?: string;
  username?: string;
  fullName?: string;
  customId?: string | null;
  Username?: string;
  FullName?: string;
  CustomId?: string | null;
};

type AuditRoleTransitionWire = {
  currentRoleId?: number | null;
  currentRoleName?: string | null;
  CurrentRoleName?: string | null;
  newRoleId?: number | null;
  newRoleName?: string | null;
  NewRoleName?: string | null;
};

/** Wire row for audit list API before {@link mapAuditDtoToEntity}. */
export interface AuditWireDto {
  id: number;
  /** Legacy / alternate time field from UM list DTOs */
  timestamp?: string;
  utcCreatedOn?: string;
  userId: number;
  userName?: string;
  userFullName?: string;
  /** Actor who initiated the action (BE PerformedByUsername, camelCase JSON). TROC-191. */
  performedByUsername?: string | null;
  action: string;
  targetType: string;
  targetName: string;
  targetUser?: AuditUserRef | string | null;
  /** TROC-353 — top-level Custom ID when not nested under targetUser. */
  targetUserCustomId?: string | null;
  targetDatabase?: string | null;
  roleTransition?: AuditRoleTransitionWire | null;
  /** Some .NET serializers emit PascalCase for nested objects. */
  RoleTransition?: AuditRoleTransitionWire | null;
  businessJustification?: string | null;
  /** Target user role deviation after this change (TROC-351). */
  hasRoleDeviation?: boolean | null;
  previousValue?: string | null;
  newValue?: string | null;
  /** Raw JSON or text payload when permission changes are applied */
  changes?: string | null;
  ipAddress?: string | null;
  computerName?: string | null;
  operatingSystem?: string | null;
  browser?: string | null;
  justification?: string | null;
  changeCategory?: string | null;
  approvalRequestId: number | null;
  isReviewed: boolean;
  reviewedBy?: string | unknown | null;
  reviewedAt?: string | null;
  utcReviewedOn?: string | null;
}

function reviewedByDisplayName(v: string | unknown | null | undefined): string | null {
  if (v == null) return null;
  if (typeof v === 'string') {
    const s = v.trim();
    return s === '' ? null : s;
  }
  if (typeof v === 'object') {
    const o = v as AuditUserRef;
    const fullName = (o.fullName ?? o.FullName ?? '').trim();
    const username = (o.username ?? o.Username ?? '').trim();
    if (fullName && username && fullName.toLowerCase() !== username.toLowerCase()) {
      return `${fullName} (${username})`;
    }
    return fullName || username || null;
  }
  return null;
}

function parseCustomIdFromUnknown(value: unknown): string | null {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed === '' ? null : trimmed;
}

function parseUserRefFromWire(value: unknown): { username: string; fullName: string | null; customId: string | null } {
  if (value == null) return { username: '', fullName: null, customId: null };
  if (typeof value === 'string') return { username: value.trim(), fullName: null, customId: null };
  if (typeof value === 'object') {
    const o = value as AuditUserRef;
    const username = String(o.username ?? o.Username ?? '').trim();
    const fullNameRaw = String(o.fullName ?? o.FullName ?? '').trim();
    const customId = parseCustomIdFromUnknown(o.customId ?? o.CustomId);
    return { username, fullName: fullNameRaw !== '' ? fullNameRaw : null, customId };
  }
  return { username: '', fullName: null, customId: null };
}

function parseAuditTargetUserFromWire(dto: AuditWireDto): {
  username: string;
  fullName: string | null;
  customId: string | null;
} {
  const ref = dto.targetUser;
  let username = (dto.targetName ?? '').trim();
  let fullName: string | null = null;
  let customId = parseCustomIdFromUnknown(dto.targetUserCustomId);

  if (ref != null) {
    const parsed = parseUserRefFromWire(ref);
    if (parsed.username !== '') username = parsed.username;
    fullName = parsed.fullName;
    if (parsed.customId != null) customId = parsed.customId;
  }

  return { username, fullName, customId };
}

function coalesceLegacyUserDisplayName(dto: AuditWireDto): string {
  const a = (dto.userName ?? '').trim();
  const b = (dto.userFullName ?? '').trim();
  if (a && b && a.toLowerCase() !== b.toLowerCase()) {
    return `${a} (${b})`;
  }
  return a || b;
}

/**
 * TROC-191 / TROC-285: resolve the actor login used for the self-review gate.
 * Legacy rows only expose `performedByUsername`. Enriched rows (TROC-285) may put the actor on `userName`.
 */
export function resolveAuditActorUsername(source: {
  performedByUsername?: string | null;
  userName?: string | null;
  targetUser?: unknown;
  targetDatabase?: string | null;
  roleTransition?: unknown;
  businessJustification?: string | null;
}): string | null {
  const performed = (source.performedByUsername ?? '').trim();
  if (performed !== '') return performed;

  const enriched =
    source.targetUser != null || source.targetDatabase != null || source.roleTransition != null || source.businessJustification != null;
  if (enriched) {
    const actor = (source.userName ?? '').trim();
    return actor !== '' ? actor : null;
  }

  return null;
}

function resolvePerformedByFields(dto: AuditWireDto): {
  performedByUsername: string | null;
  performedByFullName: string | null;
  performedByDisplayName: string | null;
  legacyUserName: string;
} {
  const performedByUsername = resolveAuditActorUsername(dto);
  const performedByFullName = (dto.userFullName ?? '').trim() || null;
  const performedByDisplayName = performedByUsername ?? (coalesceLegacyUserDisplayName(dto) || null);
  const legacyUserName = performedByDisplayName ?? coalesceLegacyUserDisplayName(dto);

  return {
    performedByUsername,
    performedByFullName,
    performedByDisplayName,
    legacyUserName
  };
}

function trimRoleName(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim();
  return trimmed === '' ? null : trimmed;
}

function mapRoleTransition(dto: AuditWireDto): { from: string | null; to: string | null } {
  const rt = dto.roleTransition ?? dto.RoleTransition ?? null;
  if (rt != null) {
    const from = trimRoleName(rt.currentRoleName ?? rt.CurrentRoleName);
    const to = trimRoleName(rt.newRoleName ?? rt.NewRoleName);
    if (from != null || to != null) {
      return { from, to };
    }
  }

  // Bulk Initial Import sometimes only populates previousValue/newValue (role names).
  if (dto.action === 'ApplyUserRoleChange') {
    const from = trimRoleName(dto.previousValue);
    const toRaw = trimRoleName(dto.newValue);
    const to = toRaw != null && !toRaw.startsWith('{') && !toRaw.startsWith('[') ? toRaw : null;
    if (from != null || to != null) {
      return { from, to };
    }
  }

  return { from: null, to: null };
}

export function mapAuditDtoToEntity(dto: AuditWireDto): AuditLogEntry {
  const timestamp = (dto.timestamp ?? dto.utcCreatedOn ?? '').trim();
  const newValue = dto.newValue ?? dto.changes ?? null;
  const changeCategory = (dto.changeCategory as ChangeCategory | null | undefined) ?? null;
  const actorFields = resolvePerformedByFields(dto);
  const targetUser = parseAuditTargetUserFromWire(dto);
  const roleTransition = mapRoleTransition(dto);
  const businessJustification = (dto.businessJustification ?? dto.justification ?? null)?.trim() || null;
  const targetDatabase = (dto.targetDatabase ?? '').trim() || null;
  const hasRoleDeviation = typeof dto.hasRoleDeviation === 'boolean' ? dto.hasRoleDeviation : null;

  return {
    id: dto.id,
    timestamp: timestamp || new Date().toISOString(),
    userId: dto.userId,
    userName: actorFields.legacyUserName,
    performedByUsername: actorFields.performedByUsername,
    performedByFullName: actorFields.performedByFullName,
    performedByDisplayName: actorFields.performedByDisplayName,
    action: dto.action as AuditAction,
    targetType: dto.targetType,
    targetName: targetUser.username || dto.targetName,
    targetUserUsername: targetUser.username || null,
    targetUserFullName: targetUser.fullName,
    targetUserCustomId: targetUser.customId,
    targetDatabase,
    roleTransitionFrom: roleTransition.from,
    roleTransitionTo: roleTransition.to,
    businessJustification,
    hasRoleDeviation,
    previousValue: dto.previousValue ?? null,
    newValue,
    ipAddress: dto.ipAddress ?? null,
    computerName: dto.computerName ?? null,
    operatingSystem: dto.operatingSystem ?? null,
    browser: dto.browser ?? null,
    justification: dto.justification ?? null,
    changeCategory: changeCategory as ChangeCategory | null,
    approvalRequestId: dto.approvalRequestId,
    isReviewed: dto.isReviewed,
    reviewedBy: reviewedByDisplayName(dto.reviewedBy),
    reviewedAt: dto.reviewedAt ?? dto.utcReviewedOn ?? null
  };
}
